import { createSignal, createEffect, createMemo, Show, For, onCleanup } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import BotScoped from '../components/BotScoped';
import PluginAutoSettingsForm, {
    type SettingDef,
    type DynamicOption,
    mergePluginDefaults,
} from '../components/PluginAutoSettingsForm';
import Modal from '../components/Modal';
import { SearchInput } from '../components/SearchInput';
import { FavouriteStar } from '../components/FavouriteStar';
import { useActiveBot } from '../state/activeBot';
import { useBotFanOut } from '../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../services/resourceActions';
import { useTranslation } from '../hooks/useTranslation';
import { useApi, isOk } from '../api';
import { useTablePref } from '../hooks/useTablePrefs';
import type { BotPluginListItemDto, BotPluginSettingsItemDto, BotPluginStatusCardDto } from '../api';
import { subscribeBotEngineEvents, subscribeBotEngineStream } from '../services/botEngineEvents';
import type { StorageInfo } from '../services/inventoryApi';
import { isFeatureEnabled } from '../services/featureFlags';
import { useGrantSummary } from '../hooks/useLicense';
import styles from './BotPlugins.module.css';

interface PluginStatusEvent {
    activePlugins: string[];
    statusCards: Record<string, BotPluginStatusCardDto>;
}

export default function BotPlugins() {
    return (
        <BotScoped groupAware>
            <BotPluginsInner />
        </BotScoped>
    );
}

function BotPluginsInner() {
    const api = useApi();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { botId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const grantSummary = useGrantSummary();

    const [descriptors, setDescriptors] = createStore<BotPluginListItemDto[]>([]);
    const [search, setSearch] = createSignal('');
    const [favouriteNames, setFavouriteNames] = useTablePref<string[]>('botPlugins.favourites', []);
    const [favouritesOnly, setFavouritesOnly] = useTablePref<boolean>('botPlugins.favouritesOnly', false);
    const [botRunning, setBotRunning] = createSignal(false);
    const [settingsByPlugin, setSettingsByPlugin] = createSignal<Record<string, BotPluginSettingsItemDto>>({});
    const [edits, setEdits] = createSignal<Record<string, Record<string, unknown>>>({});
    const [dirty, setDirty] = createSignal<Record<string, boolean>>({});
    const [openPlugin, setOpenPlugin] = createSignal<string | null>(null);
    const [saving, setSaving] = createSignal<string | null>(null);
    const [toggling, setToggling] = createSignal<string | null>(null);
    const [loaded, setLoaded] = createSignal(false);

    const [availableTaskKeys, setAvailableTaskKeys] = createSignal<string[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const storageOptions = (): DynamicOption[] => storages().map(storage => ({ value: storage.id, label: storage.name }));

    const fetchPluginStates = async () => {
        const id = botId();
        if (!id) return;
        const pluginsRes = await api.botPlugin.listPlugins(id);
        if (isOk(pluginsRes.data)) {
            setDescriptors(reconcile(pluginsRes.data.value.plugins, { key: 'name' }));
            setBotRunning(pluginsRes.data.value.botRunning);
        }
    };

    const fetchAll = async () => {
        const id = botId();
        if (!id) return;
        const [, settingsRes, tasksRes, storagesRes] = await Promise.all([
            fetchPluginStates(),
            api.botPlugin.getPluginSettings(id),
            api.botTask.getAvailableTasks(id),
            api.inventory.getStorages(),
        ]);

        if (isOk(storagesRes.data)) {
            setStorages(storagesRes.data.value.storages);
        }

        if (isOk(tasksRes.data)) {
            const groups = ((tasksRes.data.value as any).available ?? []) as Array<{ plugin: string; tasks: Array<{ name: string }> }>;
            const keys: string[] = [];
            for (const g of groups) {
                for (const t of g.tasks) keys.push(`${g.plugin}/${t.name}`);
            }
            setAvailableTaskKeys(keys);
        }

        if (isOk(settingsRes.data)) {
            const list = settingsRes.data.value.plugins;
            const prevMap = settingsByPlugin();
            const map: Record<string, BotPluginSettingsItemDto> = {};
            const currentEdits = edits();
            const currentDirty = dirty();
            const currentOpen = openPlugin();
            const nextEdits: Record<string, Record<string, unknown>> = {};
            for (const ps of list) {
                const prev = prevMap[ps.plugin];
                map[ps.plugin] = prev && JSON.stringify(prev.definitions) === JSON.stringify(ps.definitions)
                    ? { ...ps, definitions: prev.definitions }
                    : ps;
                const preserve = currentDirty[ps.plugin] || currentOpen === ps.plugin;
                nextEdits[ps.plugin] = preserve && currentEdits[ps.plugin]
                    ? currentEdits[ps.plugin]
                    : mergePluginDefaults(ps.values as Record<string, unknown>, ps.definitions as unknown as SettingDef[]);
            }
            setSettingsByPlugin(map);
            setEdits(nextEdits);
        }
        setLoaded(true);
    };

    const changedValues = (pluginName: string, values: Record<string, unknown>): Record<string, unknown> => {
        const loaded = settingsByPlugin()[pluginName];
        const baseline = loaded
            ? mergePluginDefaults(loaded.values as Record<string, unknown>, loaded.definitions as unknown as SettingDef[])
            : {};
        return Object.fromEntries(Object.entries(values)
            .filter(([settingName, value]) => JSON.stringify(value) !== JSON.stringify(baseline[settingName])));
    };

    // Other members keep their own settings and take only the fields edited on the focused bot.
    const saveToMember = async (memberBotId: string, pluginName: string, changed: Record<string, unknown>) => {
        const settingsRes = await api.botPlugin.getPluginSettings(memberBotId);
        if (!isOk(settingsRes.data)) return settingsRes;
        const memberValues = settingsRes.data.value.plugins.find(p => p.plugin === pluginName)?.values ?? {};
        return api.botPlugin.savePluginSettings(memberBotId, pluginName, { settings: { ...memberValues, ...changed } as never });
    };

    const save = async (pluginName: string) => {
        const id = botId();
        if (!id) return;
        const values = edits()[pluginName] ?? {};
        const changed = changedValues(pluginName, values);
        setSaving(pluginName);
        const failedIds = await fanOut.run(targetIds(BOT_ACTIONS.ConfigWrite),
            targetBotId => targetBotId === id
                ? api.botPlugin.savePluginSettings(id, pluginName, { settings: values as never })
                : saveToMember(targetBotId, pluginName, changed),
            { success: t('BOT_PLUGINS_SAVED'), failure: t('BOT_PLUGINS_SAVE_FAILED') });
        setSaving(null);
        if (!failedIds.includes(id)) {
            setDirty(prev => ({ ...prev, [pluginName]: false }));
            await fetchAll();
        }
    };

    const discardEdits = (pluginName: string) => {
        const ps = settingsByPlugin()[pluginName];
        if (ps) {
            setEdits(prev => ({
                ...prev,
                [pluginName]: mergePluginDefaults(ps.values as Record<string, unknown>, ps.definitions as unknown as SettingDef[]),
            }));
        }
        setDirty(prev => ({ ...prev, [pluginName]: false }));
    };

    const reloadPluginValues = async (pluginName: string) => {
        await fetchAll();
        discardEdits(pluginName);
    };

    const setPluginValues = (plugin: string, next: Record<string, unknown>) => {
        setEdits(prev => ({ ...prev, [plugin]: next }));
        setDirty(prev => ({ ...prev, [plugin]: true }));
    };

    const toggleEnabled = async (descriptor: BotPluginListItemDto) => {
        const id = botId();
        if (!id || isLocked(descriptor)) return;
        setToggling(descriptor.name);
        await fanOut.run(targetIds(BOT_ACTIONS.ConfigWrite),
            targetBotId => descriptor.enabled
                ? api.botPlugin.disablePlugin(targetBotId, descriptor.name)
                : api.botPlugin.enablePlugin(targetBotId, descriptor.name),
            { failure: t('BOT_PLUGINS_TOGGLE_FAILED') });
        setToggling(null);
        await fetchAll();
    };

    const formatStatusText = (key: string | null | undefined, args: readonly string[] | undefined | null) => {
        if (!key) return '';
        return args && args.length > 0 ? t(key, ...args) : t(key);
    };

    const liveState = (d: BotPluginListItemDto) =>
        botRunning() ? (d.activeInRunningBot ?? d.enabled) : d.enabled;

    const pendingRestart = (d: BotPluginListItemDto) =>
        botRunning() && d.activeInRunningBot != null && d.activeInRunningBot !== d.enabled;

    const hasLicense = (type: string) => {
        const entry = grantSummary()?.entries.find(e => e.type === type);
        return !!entry && entry.effectiveValue > 0;
    };

    const isLocked = (d: BotPluginListItemDto) =>
        (!!d.requiredFeatureFlag && !isFeatureEnabled(d.requiredFeatureFlag)) ||
        (!!d.requiredLicense && !hasLicense(d.requiredLicense));

    const isFavourite = (name: string) => favouriteNames().includes(name);

    const toggleFavourite = (name: string) => {
        setFavouriteNames(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
    };

    const favouritesFirst = (plugins: BotPluginListItemDto[]) =>
        [...plugins].sort((left, right) => Number(isFavourite(right.name)) - Number(isFavourite(left.name)));

    const favouriteTitle = (name: string) =>
        isFavourite(name) ? t('BOT_PLUGINS_FAVOURITE_REMOVE') : t('BOT_PLUGINS_FAVOURITE_ADD');

    const toggleTitle = (d: BotPluginListItemDto) => {
        if (d.enabled) {
            return botRunning() ? t('BOT_PLUGINS_TOGGLE_DISABLE_RESTART') : t('BOT_PLUGINS_TOGGLE_DISABLE');
        }
        return botRunning() ? t('BOT_PLUGINS_TOGGLE_ENABLE_RESTART') : t('BOT_PLUGINS_TOGGLE_ENABLE');
    };

    const editPlugin = (descriptor: BotPluginListItemDto) => {
        if (descriptor.customPagePath) {
            navigate(descriptor.customPagePath);
            return;
        }
        setOpenPlugin(openPlugin() === descriptor.name ? null : descriptor.name);
    };

    const hasEditableSettings = (d: BotPluginListItemDto) =>
        (settingsByPlugin()[d.name]?.definitions.length ?? 0) > 0;

    createEffect(() => {
        if (botId()) void fetchAll();
    });

    // Loaded plugin names and catalog names are matched case-insensitively on the server too.
    const applyPluginStatus = (status: PluginStatusEvent) => {
        const activeNames = new Set(status.activePlugins.map(name => name.toLowerCase()));
        const cardsByName = new Map(Object.entries(status.statusCards).map(([name, card]) => [name.toLowerCase(), card]));
        setBotRunning(true);
        setDescriptors(reconcile(descriptors.map(descriptor => ({
            ...descriptor,
            activeInRunningBot: activeNames.has(descriptor.name.toLowerCase()),
            statusCard: cardsByName.get(descriptor.name.toLowerCase()),
        })), { key: 'name' }));
    };

    createEffect(() => {
        const id = botId();
        if (!id) return;
        const stopEvents = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type === 'plugin_status') applyPluginStatus(evt.payload);
            else if (evt.type === 'status_changed' && (evt.payload.state === 'Disconnected' || evt.payload.state === 'Error')) setBotRunning(false);
        });
        let seenEpoch: number | null = null;
        const stopStream = subscribeBotEngineStream(id, (stream) => {
            if (seenEpoch !== null && stream.epoch !== seenEpoch) void fetchPluginStates();
            seenEpoch = stream.epoch;
        });
        onCleanup(() => { stopEvents(); stopStream(); });
    });

    const filteredDescriptors = createMemo(() => {
        const q = search().trim().toLowerCase();
        const pluginsMatchingSearch = q ? descriptors.filter(d => d.name.toLowerCase().includes(q)) : descriptors;
        return favouritesOnly() ? pluginsMatchingSearch.filter(d => isFavourite(d.name)) : pluginsMatchingSearch;
    });

    const userFacing = () => favouritesFirst(filteredDescriptors().filter(d => d.isUserFacing));
    const infraWithSettings = () => favouritesFirst(filteredDescriptors().filter(d => !d.isUserFacing && d.hasSettings));
    const hasSearchResults = () => userFacing().length > 0 || infraWithSettings().length > 0;

    return (
        <div class={styles.container}>
            <div class={styles.header}>
                <h1><Text val="BOT_PLUGINS_TITLE" /></h1>
            </div>

            <Show when={loaded()} fallback={
                <div class={styles.grid}>
                    <For each={[0, 1, 2]}>{() =>
                        <div class={`${styles.card} ${styles.skeletonCard}`}>
                            <div class={styles.skeletonLine} style={{ width: '40%' }} />
                            <div class={styles.skeletonLine} style={{ width: '70%' }} />
                            <div class={styles.skeletonLine} style={{ width: '30%' }} />
                        </div>
                    }</For>
                </div>
            }>
            <Show
                when={descriptors.length > 0}
                fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-puzzle-piece" />
                        <p><Text val="BOT_PLUGINS_NO_PLUGINS" /></p>
                    </div>
                }
            >
                <div class={styles.toolbar}>
                    <div class={styles.searchBar}>
                        <SearchInput
                            value={search()}
                            onInput={setSearch}
                            placeholder={t('BOT_PLUGINS_SEARCH_PLACEHOLDER')}
                        />
                    </div>
                    <Show when={favouriteNames().length > 0 || favouritesOnly()}>
                        <button
                            class={styles.pill}
                            classList={{ [styles.pillOn]: favouritesOnly() }}
                            onClick={() => setFavouritesOnly(!favouritesOnly())}
                            aria-pressed={favouritesOnly()}
                        >
                            <i class="fa-solid fa-star" aria-hidden="true" />{' '}
                            <Text val="BOT_PLUGINS_FAVOURITES_ONLY" /> ({favouriteNames().length})
                        </button>
                    </Show>
                </div>

                <Show
                    when={hasSearchResults()}
                    fallback={
                        <Show
                            when={favouritesOnly() && favouriteNames().length === 0}
                            fallback={
                                <div class={styles.empty}>
                                    <i class="fa-solid fa-magnifying-glass" />
                                    <p><Text val="BOT_PLUGINS_NO_SEARCH_RESULTS" /></p>
                                </div>
                            }
                        >
                            <div class={styles.empty}>
                                <i class="fa-regular fa-star" />
                                <p><Text val="BOT_PLUGINS_NO_FAVOURITES" /></p>
                            </div>
                        </Show>
                    }
                >
                <Show when={userFacing().length > 0}>
                <div class={styles.grid}>
                    <For each={userFacing()}>{(d) => <>
                        <div class={styles.card} classList={{ [styles.cardLocked]: isLocked(d) }}>
                            <div class={styles.cardHeader}>
                                <div class={styles.pluginName}>
                                    <FavouriteStar
                                        active={isFavourite(d.name)}
                                        onToggle={() => toggleFavourite(d.name)}
                                        title={favouriteTitle(d.name)}
                                    />
                                    {d.name}
                                </div>
                                <Show
                                    when={!isLocked(d)}
                                    fallback={
                                        <span class={styles.lockedBadge} title={t('BOT_PLUGINS_LOCKED_TITLE')}>
                                            <i class="fa-solid fa-lock" /> <Text val="BOT_PLUGINS_LOCKED" />
                                        </span>
                                    }
                                >
                                    <button
                                        class={styles.toggleBtn}
                                        classList={{ [styles.toggleBtnOn]: d.enabled, [styles.toggleBtnOff]: !d.enabled }}
                                        onClick={() => void toggleEnabled(d)}
                                        disabled={toggling() === d.name}
                                        title={toggleTitle(d)}
                                    >
                                        <i class={`fa-solid ${d.enabled ? 'fa-toggle-on' : 'fa-toggle-off'}`} />
                                        <Show when={d.enabled} fallback={<Text val="BOT_PLUGINS_DISABLED" />}>
                                            <Text val="BOT_PLUGINS_ENABLED" />
                                        </Show>
                                    </button>
                                </Show>
                            </div>

                            <div class={styles.liveRow}>
                                <span
                                    class={styles.liveDot}
                                    classList={{ [styles.liveDotOn]: liveState(d), [styles.liveDotOff]: !liveState(d) }}
                                />
                                <span class={styles.liveText}>
                                    <Show when={botRunning()} fallback={<Text val="BOT_PLUGINS_LIVE_BOT_OFFLINE" />}>
                                        <Show when={liveState(d)} fallback={<Text val="BOT_PLUGINS_LIVE_INACTIVE" />}>
                                            <Text val="BOT_PLUGINS_LIVE_ACTIVE" />
                                        </Show>
                                    </Show>
                                </span>
                            </div>

                            <Show when={d.statusCard} fallback={
                                <div class={styles.cardSummary}>
                                    <Show when={settingsByPlugin()[d.name]?.definitions.length} fallback={
                                        <Text val="BOT_PLUGINS_CARD_NO_SETTINGS" />
                                    }>
                                        <Text val="BOT_PLUGINS_CARD_HAS_SETTINGS" />
                                    </Show>
                                </div>
                            }>
                                <div class={styles.statusCard} data-status={d.statusCard!.status}>
                                    <div class={styles.statusHeadline}>{formatStatusText(d.statusCard!.headlineKey, d.statusCard!.headlineArgs)}</div>
                                    <Show when={d.statusCard!.sublineKey}>
                                        <div class={styles.statusSubline}>{formatStatusText(d.statusCard!.sublineKey, d.statusCard!.sublineArgs)}</div>
                                    </Show>
                                </div>
                            </Show>

                            <Show when={pendingRestart(d)}>
                                <div class={styles.restartHint}>
                                    <i class="fa-solid fa-clock-rotate-left" />{' '}
                                    <Show when={d.enabled} fallback={<Text val="BOT_PLUGINS_PENDING_DISABLE" />}>
                                        <Text val="BOT_PLUGINS_PENDING_ENABLE" />
                                    </Show>
                                </div>
                            </Show>

                            <div class={styles.cardActions}>
                                <button
                                    class="btn btn-secondary btn-sm"
                                    disabled={!d.customPagePath && !settingsByPlugin()[d.name]}
                                    onClick={() => editPlugin(d)}
                                >
                                    <i class="fa-solid fa-pen-to-square" />
                                    <Show when={d.customPagePath} fallback={<Text val="BOT_PLUGINS_EDIT" />}>
                                        <Text val="BOT_PLUGINS_OPEN" />
                                    </Show>
                                </button>
                                <Show when={d.customPagePath && hasEditableSettings(d)}>
                                    <button
                                        class="btn btn-secondary btn-sm"
                                        onClick={() => setOpenPlugin(openPlugin() === d.name ? null : d.name)}
                                        title={t('BOT_PLUGINS_SETTINGS')}
                                    >
                                        <i class="fa-solid fa-sliders" /> <Text val="BOT_PLUGINS_SETTINGS" />
                                    </button>
                                </Show>
                            </div>
                        </div>
                    </>}</For>
                </div>
            </Show>

            <Show when={infraWithSettings().length > 0}>
                <div class={styles.infraSection}>
                    <h2 class={styles.infraTitle}><Text val="BOT_PLUGINS_INFRA_TITLE" /></h2>
                    <p class={styles.infraHint}><Text val="BOT_PLUGINS_INFRA_HINT" /></p>
                    <div class={styles.infraList}>
                        <For each={infraWithSettings()}>{(d) => (
                            <div class={styles.infraRow}>
                                <div class={styles.infraName}>
                                    <FavouriteStar
                                        active={isFavourite(d.name)}
                                        onToggle={() => toggleFavourite(d.name)}
                                        title={favouriteTitle(d.name)}
                                    />
                                    {d.name}
                                </div>
                                <button
                                    class="btn btn-secondary btn-sm"
                                    disabled={!settingsByPlugin()[d.name]}
                                    onClick={() => setOpenPlugin(d.name)}
                                >
                                    <i class="fa-solid fa-pen-to-square" />
                                    <Text val="BOT_PLUGINS_EDIT" />
                                </button>
                            </div>
                        )}</For>
                    </div>
                </div>
            </Show>
                </Show>
                </Show>
            </Show>

            <Modal
                open={!!openPlugin() && !!settingsByPlugin()[openPlugin()!]}
                onClose={() => {
                    const name = openPlugin();
                    if (name) discardEdits(name);
                    setOpenPlugin(null);
                }}
                title={<><Text val="BOT_PLUGINS_SETTINGS_LABEL" /> · {openPlugin()}</>}
                maxWidth="640px"
            >
                <Show when={openPlugin() && settingsByPlugin()[openPlugin()!]}>
                    <PluginAutoSettingsForm
                        botId={botId()!}
                        pluginName={openPlugin()!}
                        definitions={settingsByPlugin()[openPlugin()!].definitions as unknown as SettingDef[]}
                        values={edits()[openPlugin()!] ?? {}}
                        onChange={(next) => setPluginValues(openPlugin()!, next)}
                        onSave={() => void save(openPlugin()!)}
                        onReset={() => reloadPluginValues(openPlugin()!)}
                        saving={saving() === openPlugin()}
                        saveTargetCount={targetIds(BOT_ACTIONS.ConfigWrite).length}
                        dynamicOptions={{ AvailableTasks: availableTaskKeys(), Storages: storageOptions() }}
                        storages={storages()}
                    />
                </Show>
            </Modal>
        </div>
    );
}
